import React, { useEffect, useRef, useState } from "react";
import splashVideo from "../assets/odra-splash.mp4";

// One-time animated ODRA splash shown when the dashboard first loads in a
// browser session. It plays the branded logo animation (vertical, white
// background, "Powering Smarter Hospitality") through once and dismisses when
// the video ends, then fades. A gentle speed-up keeps the ~10s source to a
// sensible splash length. sessionStorage keeps it to once per tab session
// (not on every route change).
const SEEN_KEY = "odra_splash_seen_v2"; // bumped: new animated logo
const SPEED = 1.6;     // 1 = natural (~10s); higher = shorter, lower = longer
const FADE_MS = 450;   // fade-out duration (matches the CSS transition)
const SAFETY_MS = 13000; // hard cap so a stalled video never traps the UI

export default function SplashScreen() {
  // Decide once, synchronously, whether to show at all.
  const [render, setRender] = useState(() => {
    try {
      return !sessionStorage.getItem(SEEN_KEY);
    } catch {
      return true; // private mode / storage blocked -> just show it
    }
  });
  const [visible, setVisible] = useState(true);
  const vidRef = useRef(null);

  // Play the animation; dismiss when it actually completes (or on error/safety).
  useEffect(() => {
    if (!render) return;
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {
      /* ignore */
    }

    const done = () => setVisible(false);
    const v = vidRef.current;
    if (v) {
      v.playbackRate = SPEED;
      // Some browsers need an explicit play() after mount for muted autoplay.
      const p = v.play();
      if (p && p.catch) p.catch(done);
    }
    const safety = setTimeout(done, SAFETY_MS);
    return () => clearTimeout(safety);
  }, [render]);

  // Once it's no longer visible (completed, tapped, or safety), fade then unmount.
  useEffect(() => {
    if (visible || !render) return;
    const t = setTimeout(() => setRender(false), FADE_MS);
    return () => clearTimeout(t);
  }, [visible, render]);

  if (!render) return null;

  return (
    <div
      onClick={() => setVisible(false)}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: visible ? 1 : 0,
        transition: `opacity ${FADE_MS}ms ease`,
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      <video
        ref={vidRef}
        src={splashVideo}
        autoPlay
        muted
        playsInline
        onEnded={() => setVisible(false)}
        onError={() => setVisible(false)}
        aria-label="ODRA"
        style={{
          width: "min(70vw, 340px)",
          height: "auto",
          maxHeight: "92vh",
          objectFit: "contain",
        }}
      />
    </div>
  );
}
